import { getS3FileOriginalName, resolveVaultDisplayName } from './get-s3file-original-name.util';
import { sanitizeS3FileName } from './sanitize-s3-file-name.util';

export type FileToRename = {
  link: string;
  name: string;
  oldName: string;
};

export type FileWithDisplayName = {
  doc?: { name?: string };
  docLink?: string;
};

export const resolveStorageFileName = (storedName: string | undefined, link?: string): string => {
  const display = link ? resolveVaultDisplayName(storedName, link) : storedName?.trim() || 'file';
  return sanitizeS3FileName(display);
};

export const fileWithStorageDisplayName = (file: File, link: string): File => {
  const name = getS3FileOriginalName(link) || sanitizeS3FileName(file.name);
  return new File([file], name, { type: file.type });
};

export const getFileDisplayName = (doc?: { name?: string }): string => doc?.name?.trim() || '';

export const collectFormFileNames = (files: FileWithDisplayName[]): string[] =>
  files.map((file) => getFileDisplayName(file.doc)).filter(Boolean);

export const findDuplicateNames = (names: string[]): Set<string> => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const name of names) {
    if (seen.has(name)) {
      duplicates.add(name);
    } else {
      seen.add(name);
    }
  }

  return duplicates;
};

export const getResolvedFileDisplayName = (
  fileName: string | undefined,
  filesToRename: FileToRename[],
  docLink?: string,
): string | undefined => {
  if (!fileName) {
    return undefined;
  }

  if (docLink) {
    const byLink = filesToRename.find((entry) => entry.link === docLink);
    return byLink ? byLink.name : fileName;
  }

  return filesToRename.find((entry) => entry.oldName === fileName)?.name ?? fileName;
};

export const collectResolvedFormFileNames = (
  files: FileWithDisplayName[],
  filesToRename: FileToRename[],
): string[] =>
  files
    .map((file) =>
      getResolvedFileDisplayName(getFileDisplayName(file.doc), filesToRename, file.docLink),
    )
    .filter((name): name is string => Boolean(name));

export const collectResolvedStorageFileNames = (
  files: FileWithDisplayName[],
  filesToRename: FileToRename[],
): string[] =>
  files
    .map((file) => {
      const displayName = getResolvedFileDisplayName(
        getFileDisplayName(file.doc),
        filesToRename,
        file.docLink,
      );
      if (!displayName) return undefined;
      return resolveStorageFileName(displayName, file.docLink);
    })
    .filter((name): name is string => Boolean(name));

export const hasDuplicateNamesInForm = (
  files: FileWithDisplayName[],
  filesToRename: FileToRename[],
): boolean => {
  const resolvedNames = collectResolvedStorageFileNames(files, filesToRename);
  return findDuplicateNames(resolvedNames).size > 0;
};

export const isFileNameTakenInForm = (fileName: string, existingNames: string[]): boolean =>
  existingNames.includes(resolveStorageFileName(fileName));

// Saved files enter the form as a plain `{ name, size, type }` doc next to their link; a new upload
// keeps the dropped `File`.
const isSavedFile = (file: FileWithDisplayName): boolean =>
  Boolean(file.docLink) && !(file.doc instanceof File);

export const hasUniqueFileDisplayNames = (files: FileWithDisplayName[] | undefined): boolean => {
  if (!files?.length) return true;

  const filesByStorageName = new Map<string, FileWithDisplayName[]>();
  files.forEach((file) => {
    const name = resolveStorageFileName(getFileDisplayName(file.doc), file.docLink);
    filesByStorageName.set(name, [...(filesByStorageName.get(name) ?? []), file]);
  });

  // Files already saved on the event live under distinct links even when their names sanitize to
  // the same storage name (e.g. "My File.pdf" and "My_File.pdf"). Flagging those disabled Update
  // on the whole edit form with no way to see why, so only a collision involving a new upload counts.
  return Array.from(filesByStorageName.values()).every(
    (sameName) => sameName.length < 2 || sameName.every(isSavedFile),
  );
};

export const findFirstDuplicateInDrop = (
  acceptedFiles: File[],
  existingNames: string[],
): File | undefined => {
  const seenInBatch = new Set<string>();
  const existingStorageNames = existingNames.map((name) => resolveStorageFileName(name));

  for (const file of acceptedFiles) {
    const storageName = resolveStorageFileName(file.name);
    if (existingStorageNames.includes(storageName) || seenInBatch.has(storageName)) {
      return file;
    }
    seenInBatch.add(storageName);
  }

  return undefined;
};

export const isFileNameConflictWithExisting = ({
  doc,
  docLink,
  existingNames,
  filesToRename,
}: {
  doc?: { name?: string };
  docLink?: string;
  existingNames: string[];
  filesToRename: FileToRename[];
}): boolean => {
  const name = getFileDisplayName(doc);
  if (!name) return false;

  const storageName = resolveStorageFileName(name, docLink);
  const existingStorageNames = existingNames.map((existingName) =>
    resolveStorageFileName(existingName),
  );
  if (!existingStorageNames.includes(storageName)) return false;
  return !filesToRename.some(
    (entry) => entry.oldName === name || (docLink && entry.link === docLink),
  );
};
